import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { UserPlus, CheckCircle } from 'lucide-react';
import { useApp } from '../../context/AppContext';
import { generateId } from '../../utils/helpers';

const emptyForm = {
  // Personal
  firstName: '', lastName: '', dob: '', gender: 'boy', bloodGroup: 'A+',
  phone: '', email: '', address: '',
  // Identity
  category: 'General', caste: '', religion: '', aadharNumber: '', penNumber: '',
  // Academic
  classId: '', sectionId: 'A', rollNumber: '',
  admissionDate: new Date().toISOString().split('T')[0],
  admissionNumber: '', status: 'active', feeGroup: '', feeStructureId: '',
  // Father
  fatherName: '', fatherOccupation: '', fatherPhone: '',
  // Mother
  motherName: '', motherOccupation: '', motherPhone: '',
  // Guardian
  guardianType: 'father', guardianName: '', guardianRelation: '', guardianPhone: '', guardianOccupation: '',
  // Parent link
  parentId: '',
};

function Field({ label, children, span = 1 }) {
  const spanClass = span === 3 ? 'col-span-3' : span === 2 ? 'col-span-2' : '';
  return (
    <div className={spanClass}>
      <label className="block text-sm font-medium mb-1">{label}</label>
      {children}
    </div>
  );
}

function SectionCard({ number, title, children }) {
  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
      <h2 className="text-base font-semibold mb-4 flex items-center gap-2">
        <span className="w-6 h-6 bg-indigo-100 text-indigo-700 rounded-full flex items-center justify-center font-bold flex-shrink-0">{number}</span>
        {title}
      </h2>
      <div className="grid grid-cols-3 gap-4">
        {children}
      </div>
    </div>
  );
}

const inputCls = 'w-full px-3 py-1 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-300';

function NewAdmission() {
  const { students, setStudents, classes, parents, feeStructures } = useApp();
  const navigate = useNavigate();
  const [form, setForm] = useState({ ...emptyForm, admissionNumber: `ADM${Date.now()}` });
  const [saved, setSaved] = useState(false);
  const [savedName, setSavedName] = useState('');

  const set = (key, val) => setForm(f => ({ ...f, [key]: val }));

  const handleSubmit = (e) => {
    e.preventDefault();
    setStudents(prev => [...prev, { ...form, id: generateId('STU') }]);
    setSavedName(`${form.firstName} ${form.lastName}`);
    setSaved(true);
  };

  const handleAnother = () => {
    setForm({ ...emptyForm, admissionNumber: `ADM${Date.now()}` });
    setSaved(false);
    setSavedName('');
  };

  if (saved) {
    return (
      <div className="max-w-lg mx-auto mt-16 text-center">
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-10">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <CheckCircle size={32} className="text-green-600" />
          </div>
          <h2 className="text-xl font-bold mb-1">Admission Successful</h2>
          <p className="text-gray-500 text-sm mb-6">{savedName} has been admitted successfully.</p>
          <div className="flex justify-center gap-3">
            <button onClick={handleAnother} className="px-5 py-2 bg-indigo-600 text-white rounded-lg text-sm font-medium hover:bg-indigo-700">
              New Admission
            </button>
            <button onClick={() => navigate('/students')} className="px-5 py-2 border border-gray-300 rounded-lg text-sm font-medium hover:bg-gray-50">
              View All Students
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-2xl font-bold">New Admission</h1>
        <p className="text-gray-500 text-sm mt-0.5">Register a new student into the school</p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">

        {/* 1. Personal Information */}
        <SectionCard number="1" title="Personal Information">
          <Field label="First Name">
            <input required value={form.firstName} onChange={e => set('firstName', e.target.value)} className={inputCls} placeholder="e.g. Arjun" />
          </Field>
          <Field label="Last Name">
            <input required value={form.lastName} onChange={e => set('lastName', e.target.value)} className={inputCls} placeholder="e.g. Kumar" />
          </Field>
          <Field label="Date of Birth">
            <input required type="date" value={form.dob} onChange={e => set('dob', e.target.value)} className={inputCls} />
          </Field>
          <Field label="Gender">
            <select value={form.gender} onChange={e => set('gender', e.target.value)} className={inputCls}>
              {['boy','girl'].map(o => <option key={o} value={o}>{o.charAt(0).toUpperCase()+o.slice(1)}</option>)}
            </select>
          </Field>
          <Field label="Blood Group">
            <select value={form.bloodGroup} onChange={e => set('bloodGroup', e.target.value)} className={inputCls}>
              {['A+','A-','B+','B-','O+','O-','AB+','AB-'].map(o => <option key={o}>{o}</option>)}
            </select>
          </Field>
          <Field label="Phone">
            <input type="tel" value={form.phone} onChange={e => set('phone', e.target.value)} className={inputCls} placeholder="e.g. 9876543210" />
          </Field>
          <Field label="Email" span={2}>
            <input type="email" value={form.email} onChange={e => set('email', e.target.value)} className={inputCls} placeholder="student@email.com" />
          </Field>
          <Field label="Address" span={3}>
            <input value={form.address} onChange={e => set('address', e.target.value)} className={inputCls} placeholder="Full residential address" />
          </Field>
          {/* Divider */}
          <div className="col-span-3 border-t border-gray-100 pt-2 mt-1">
            <p className="font-semibold">Father's Details</p>
          </div>
          <Field label="Father's Name">
            <input value={form.fatherName} onChange={e => set('fatherName', e.target.value)} className={inputCls} placeholder="Full name" />
          </Field>
          <Field label="Father's Occupation">
            <input value={form.fatherOccupation} onChange={e => set('fatherOccupation', e.target.value)} className={inputCls} placeholder="e.g. Engineer" />
          </Field>
          <Field label="Father's Phone">
            <input type="tel" value={form.fatherPhone} onChange={e => set('fatherPhone', e.target.value)} className={inputCls} placeholder="e.g. 9876543210" />
          </Field>
          {/* Divider */}
          <div className="col-span-3 border-t border-gray-100 pt-2 mt-1">
            <p className="font-semibold">Mother's Details</p>
          </div>
          <Field label="Mother's Name">
            <input value={form.motherName} onChange={e => set('motherName', e.target.value)} className={inputCls} placeholder="Full name" />
          </Field>
          <Field label="Mother's Occupation">
            <input value={form.motherOccupation} onChange={e => set('motherOccupation', e.target.value)} className={inputCls} placeholder="e.g. Teacher" />
          </Field>
          <Field label="Mother's Phone">
            <input type="tel" value={form.motherPhone} onChange={e => set('motherPhone', e.target.value)} className={inputCls} placeholder="e.g. 9876543210" />
          </Field>
          {/* Guardian */}
          <div className="col-span-3 border-t border-gray-100 pt-2 mt-1">
            <p className="font-semibold">Guardian / Emergency Contact</p>
          </div>
          <Field label="Guardian Is" span={3}>
            <div className="flex gap-2">
              {[['father', "Father"], ['mother', "Mother"], ['other', "Other (Custom)"]].map(([val, label]) => (
                <button key={val} type="button"
                  onClick={() => set('guardianType', val)}
                  className={`px-4 py-1 rounded-lg text-sm font-medium border transition-colors ${form.guardianType === val ? 'bg-indigo-600 text-white border-indigo-600' : 'bg-white border-gray-300 hover:bg-gray-50'}`}>
                  {label}
                </button>
              ))}
            </div>
          </Field>
          {form.guardianType === 'other' ? (
            <>
              <Field label="Guardian Name">
                <input value={form.guardianName} onChange={e => set('guardianName', e.target.value)} className={inputCls} placeholder="Full name" />
              </Field>
              <Field label="Relation">
                <input value={form.guardianRelation} onChange={e => set('guardianRelation', e.target.value)} className={inputCls} placeholder="e.g. Uncle, Grandparent" />
              </Field>
              <Field label="Guardian Phone">
                <input type="tel" value={form.guardianPhone} onChange={e => set('guardianPhone', e.target.value)} className={inputCls} placeholder="e.g. 9876543210" />
              </Field>
              <Field label="Guardian Occupation" span={2}>
                <input value={form.guardianOccupation} onChange={e => set('guardianOccupation', e.target.value)} className={inputCls} placeholder="e.g. Business" />
              </Field>
            </>
          ) : (
            <div className="col-span-3 bg-gray-50 rounded-lg px-4 py-1 text-sm">
              {form.guardianType === 'father' ? (
                form.fatherName
                  ? <span><strong>{form.fatherName}</strong> — {form.fatherOccupation || 'Occupation not filled'} &nbsp;|&nbsp; {form.fatherPhone || 'Phone not filled'}</span>
                  : <span className="text-gray-400">Fill Father's details above to auto-populate.</span>
              ) : (
                form.motherName
                  ? <span><strong>{form.motherName}</strong> — {form.motherOccupation || 'Occupation not filled'} &nbsp;|&nbsp; {form.motherPhone || 'Phone not filled'}</span>
                  : <span className="text-gray-400">Fill Mother's details above to auto-populate.</span>
              )}
            </div>
          )}
        </SectionCard>

        {/* 2. Identity & Community */}
        <SectionCard number="2" title="Identity & Community">
          <Field label="Category">
            <select value={form.category} onChange={e => set('category', e.target.value)} className={inputCls}>
              {['General','OBC','SC','ST','EWS','Other'].map(o => <option key={o}>{o}</option>)}
            </select>
          </Field>
          <Field label="Caste">
            <input value={form.caste} onChange={e => set('caste', e.target.value)} className={inputCls} placeholder="e.g. Brahmin" />
          </Field>
          <Field label="Religion">
            <select value={form.religion} onChange={e => set('religion', e.target.value)} className={inputCls}>
              <option value="">Select Religion</option>
              {['Hindu','Muslim','Christian','Sikh','Buddhist','Jain','Other'].map(o => <option key={o}>{o}</option>)}
            </select>
          </Field>
          <Field label="Aadhar Number">
            <input value={form.aadharNumber} onChange={e => set('aadharNumber', e.target.value)} className={inputCls} placeholder="12-digit Aadhar number" maxLength={14} />
          </Field>
          <Field label="PEN Number">
            <input value={form.penNumber} onChange={e => set('penNumber', e.target.value)} className={inputCls} placeholder="Permanent Education Number" />
          </Field>
        </SectionCard>

        {/* 3. Academic Details */}
        <SectionCard number="3" title="Academic Details">
          <Field label="Class">
            <select required value={form.classId} onChange={e => {
              const cid = e.target.value;
              const fs = feeStructures.find(f => f.classId === cid);
              setForm(f => ({ ...f, classId: cid, feeStructureId: fs?.id || '' }));
            }} className={inputCls}>
              <option value="">Select Class</option>
              {classes.map(c => <option key={c.id} value={c.id}>{c.name} — Section {c.section}</option>)}
            </select>
          </Field>
          <Field label="Section">
            <select value={form.sectionId} onChange={e => set('sectionId', e.target.value)} className={inputCls}>
              {['A','B','C','D'].map(s => <option key={s}>{s}</option>)}
            </select>
          </Field>
          <Field label="Roll Number">
            <input value={form.rollNumber} onChange={e => set('rollNumber', e.target.value)} className={inputCls} placeholder="e.g. 42" />
          </Field>
          <Field label="Admission Number">
            <input value={form.admissionNumber} onChange={e => set('admissionNumber', e.target.value)} className={inputCls} />
          </Field>
          <Field label="Admission Date">
            <input type="date" value={form.admissionDate} onChange={e => set('admissionDate', e.target.value)} className={inputCls} />
          </Field>
          <Field label="Fee Group">
            <select value={form.feeGroup} onChange={e => set('feeGroup', e.target.value)} className={inputCls}>
              <option value="">Select Fee Group</option>
              {['General Fee','RTE','Scholarship','Staff Ward','Other'].map(o => <option key={o}>{o}</option>)}
            </select>
          </Field>
          <Field label="Fee Structure" span={2}>
            <select value={form.feeStructureId} onChange={e => set('feeStructureId', e.target.value)} className={inputCls}>
              <option value="">Select Fee Structure</option>
              {(form.classId
                ? feeStructures.filter(fs => fs.classId === form.classId)
                : feeStructures
              ).map(fs => (
                <option key={fs.id} value={fs.id}>
                  {fs.name} — ₹{fs.totalAmount?.toLocaleString('en-IN')} ({fs.frequency})
                </option>
              ))}
            </select>
            {form.classId && feeStructures.filter(fs => fs.classId === form.classId).length === 0 && (
              <p className="text-orange-500 mt-1">No fee structure defined for this class yet.</p>
            )}
          </Field>
          <Field label="Status">
            <select value={form.status} onChange={e => set('status', e.target.value)} className={inputCls}>
              {['active','inactive','transferred','graduated'].map(s => <option key={s} value={s}>{s.charAt(0).toUpperCase()+s.slice(1)}</option>)}
            </select>
          </Field>
        </SectionCard>

        {/* 4. Parent / Guardian Link */}
        <SectionCard number="4" title="Parent / Guardian (System Link)">
          <Field label="Link to Parent Account" span={3}>
            <select value={form.parentId} onChange={e => set('parentId', e.target.value)} className={inputCls}>
              <option value="">Select Parent (optional)</option>
              {parents.map(p => <option key={p.id} value={p.id}>{p.name} — {p.relation} ({p.phone})</option>)}
            </select>
          </Field>
        </SectionCard>

        {/* Actions */}
        <div className="flex justify-end gap-3 pb-6">
          <button type="button" onClick={() => navigate('/students')} className="px-5 py-2 border border-gray-300 rounded-lg text-sm font-medium hover:bg-gray-50">
            Cancel
          </button>
          <button type="submit" className="flex items-center gap-2 px-5 py-2 bg-indigo-600 text-white rounded-lg text-sm font-medium hover:bg-indigo-700">
            <UserPlus size={16} /> Admit Student
          </button>
        </div>

      </form>
    </div>
  );
}
export default NewAdmission;
